import { toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handoffAiJobToBackground } from '@/components/ai-job/aiJobHandoff';
import type { AiJobState } from '@/lib/aiJobSocket';

interface GenerateTranslationsParams {
  attractionId: string;
  themeId: string;
  targetLanguageIds: string[];
}

interface DispatchResponse {
  success: boolean;
  data?: AiJobState;
  error?: string;
}

/**
 * Hook for generating theme translations to other languages. The endpoint
 * dispatches an Inngest job and returns 202 immediately; this hook hands
 * the resulting job off to the global notification provider so the user
 * gets a toast when the job actually finishes.
 */
export function useGenerateTranslations() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      attractionId,
      themeId,
      targetLanguageIds,
    }: GenerateTranslationsParams): Promise<DispatchResponse> => {
      return apiClient.post<DispatchResponse>(
        `/attractions/${attractionId}/themes/${themeId}/generate-translations`,
        { targetLanguageIds },
      );
    },
    onSuccess: (response, variables) => {
      if (response.data?.jobId) {
        handoffAiJobToBackground(response.data.jobId);
      }
      queryClient.invalidateQueries({
        queryKey: ['AttractionTheme', variables.attractionId],
      });
      queryClient.invalidateQueries({ queryKey: ['AttractionTheme'] });
      toast.success("Theme translations started — we'll notify you when they're ready.");
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start theme translations');
    },
  });
}
